<template>
  <div class="zoom-controls-container">
    <!-- Main Zoom Controls -->
    <div class="zoom-controls">
      <!-- Zoom In Button -->
      <button
        class="zoom-button"
        :disabled="!canZoomIn"
        @click="zoomIn"
        title="Zoom in"
      >
        <ThemeIcon icon="plus" type="icons" classes="w-4 h-4" />
      </button>

      <!-- Zoom Level Indicator -->
      <div 
        class="zoom-indicator"
        @mouseenter="handleMouseEnter"
        @mouseleave="handleMouseLeave"
      >
        <!-- Zoom Track -->
        <div class="zoom-track">
          <div 
            class="zoom-thumb" 
            :style="{ top: zoomThumbPosition + 'px' }"
          ></div>
        </div>
        
        <!-- Current Zoom Display -->
        <span class="zoom-level-text">{{ currentZoom }}</span>
        
        <!-- Zone Level Popup (on hover) -->
        <transition name="fade">
          <div v-if="showZonePopup" class="zone-popup">
            <div class="zone-popup-title">Zoom level</div>
            
            <div class="zone-popup-levels">
              <div 
                v-for="layer in zoomLevelLayers" 
                :key="layer.name"
                class="zone-popup-level-item"
                :class="{ 'active': layer.active }"
              >
                <span class="zone-popup-level-name">{{ layer.name }}</span>
                <span class="zone-popup-level-range">{{ layer.zoomRange }}</span>
              </div>
            </div>
          </div>
        </transition>
      </div>

      <!-- Zoom Out Button -->
      <button
        class="zoom-button"
        :disabled="!canZoomOut"
        @click="zoomOut"
        title="Zoom out"
      >
        <ThemeIcon icon="minus" type="icons" classes="w-4 h-4" />
      </button>
    </div>

    <!-- Additional Controls -->
    <div class="additional-controls">
      <!-- Home/Reset Button -->
      <button
        class="control-button"
        @click="resetToHome"
        title="Reset to home view"
      >
        <ThemeIcon icon="home" type="icons" classes="w-4 h-4" />
      </button>

      <!-- Current Location Button -->
      <button
        class="control-button"
        :disabled="gettingLocation"
        @click="zoomToCurrentLocation"
        title="Zoom to current location"
      >
        <ThemeIcon v-if="!gettingLocation" icon="location" type="icons" classes="w-4 h-4" />
        <svg v-else class="icon spinner" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </button>

      <!-- Show Legend Button -->
      <button
        v-if="hasActiveFilters && !showLegend"
        class="control-button"
        @click="toggleLegend"
        title="Show map legend"
      >
        <ThemeIcon icon="layers" type="icons" classes="w-4 h-4" />
      </button>
    </div>
  </div>
</template>

<script>
import ThemeIcon from '../ThemeIcon.vue';
import cresiteiq2Api, { ZOOM_THRESHOLDS } from '../../../cresiteiq2/services/cresiteiq2Api.js';

export default {
  name: 'ZoomControls',
  
  components: {
    ThemeIcon
  },

  props: {
    currentZoom: {
      type: Number,
      default: 4
    },
    minZoom: {
      type: Number,
      default: 4
    },
    maxZoom: {
      type: Number,
      default: 18
    },
    initialCenter: {
      type: Object,
      default: () => ({ lat: 39.8283, lng: -98.5795 })
    },
    hasActiveFilters: {
      type: Boolean,
      default: false
    },
    showLegend: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      gettingLocation: false,
      showZonePopup: false,
      trackHeight: 64 // Height of the zoom track in pixels (5rem = 80px, minus padding)
    };
  },

  computed: {
    canZoomIn() {
      return this.currentZoom < this.maxZoom;
    },

    canZoomOut() {
      return this.currentZoom > this.minZoom;
    },

    zoomThumbPosition() {
      // Calculate thumb position from top (inverted - higher zoom = higher position)
      const zoomRange = this.maxZoom - this.minZoom;
      const zoomOffset = this.currentZoom - this.minZoom;
      const percentage = zoomOffset / zoomRange;
      
      // Invert so higher zoom is at top (8px from top, trackHeight - 8px from bottom)
      return 8 + (this.trackHeight - 16) * (1 - percentage);
    },

    currentZoneLevel() {
      // Use the cresiteiq2Api service to determine the active layer
      return cresiteiq2Api.determineActiveLayer(this.currentZoom);
    },

    zoomLevelLayers() {
      return [
        {
          name: 'Blocks',
          zoomRange: `${ZOOM_THRESHOLDS.BLOCK_MIN}+`,
          active: this.currentZoom >= ZOOM_THRESHOLDS.BLOCK_MIN
        },
        {
          name: 'Block Groups', 
          zoomRange: `${ZOOM_THRESHOLDS.BG_MIN}-${ZOOM_THRESHOLDS.BG_MAX}`,
          active: this.currentZoom >= ZOOM_THRESHOLDS.BG_MIN && this.currentZoom <= ZOOM_THRESHOLDS.BG_MAX
        },
        {
          name: 'Census Tracts',
          zoomRange: `${ZOOM_THRESHOLDS.TRACT_MIN}-${ZOOM_THRESHOLDS.TRACT_MAX}`, 
          active: this.currentZoom >= ZOOM_THRESHOLDS.TRACT_MIN && this.currentZoom <= ZOOM_THRESHOLDS.TRACT_MAX
        },
        {
          name: 'ZIP Codes',
          zoomRange: `${ZOOM_THRESHOLDS.ZIP_MIN}-${ZOOM_THRESHOLDS.ZIP_MAX}`,
          active: this.currentZoom >= ZOOM_THRESHOLDS.ZIP_MIN && this.currentZoom <= ZOOM_THRESHOLDS.ZIP_MAX
        },
        {
          name: 'Counties (Not Available)',
          zoomRange: `${ZOOM_THRESHOLDS.COUNTY_MIN}-${ZOOM_THRESHOLDS.COUNTY_MAX}`,
          active: this.currentZoom >= ZOOM_THRESHOLDS.COUNTY_MIN && this.currentZoom <= ZOOM_THRESHOLDS.COUNTY_MAX
        },
        {
          name: 'States',
          zoomRange: `${ZOOM_THRESHOLDS.STATE_MIN}-${ZOOM_THRESHOLDS.STATE_MAX}`,
          active: this.currentZoom >= ZOOM_THRESHOLDS.STATE_MIN && this.currentZoom <= ZOOM_THRESHOLDS.STATE_MAX
        }
      ];
    }
  },

  methods: {
    handleMouseEnter() {
      console.log('Mouse entered zoom indicator');
      this.showZonePopup = true;
    },
    
    handleMouseLeave() {
      console.log('Mouse left zoom indicator');
      this.showZonePopup = false;
    },
    
    zoomIn() {
      if (this.canZoomIn) {
        this.$emit('zoom-changed', { zoom: this.currentZoom + 1 });
      }
    },

    zoomOut() {
      if (this.canZoomOut) {
        this.$emit('zoom-changed', { zoom: this.currentZoom - 1 });
      }
    },

    resetToHome() {
      this.$emit('center-changed', { center: this.initialCenter });
      this.$emit('zoom-changed', { zoom: ZOOM_THRESHOLDS.STATE_MIN });
    },

    async zoomToCurrentLocation() {
      if (!('geolocation' in navigator)) {
        this.$toast?.error('Geolocation is not supported by your browser');
        return;
      }

      this.gettingLocation = true;

      try {
        const position = await this.getCurrentPosition();
        const center = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };

        this.$emit('center-changed', { center });
        this.$emit('zoom-changed', { zoom: ZOOM_THRESHOLDS.BLOCK_MIN });

        if (this.$toast) {
          this.$toast.success('Location found');
        }
      } catch (error) {
        this.handleGeolocationError(error);
      } finally {
        this.gettingLocation = false;
      }
    },

    getCurrentPosition() {
      return new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          resolve,
          reject,
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0
          }
        );
      });
    },

    handleGeolocationError(error) {
      let message = 'Unable to get your location';

      switch (error.code) {
        case error.PERMISSION_DENIED:
          message = 'Location permission denied';
          break;
        case error.POSITION_UNAVAILABLE:
          message = 'Location information unavailable';
          break;
        case error.TIMEOUT:
          message = 'Location request timed out';
          break;
      }

      if (this.$toast) {
        this.$toast.error(message);
      }
    },

    toggleLegend() {
      this.$emit('toggle-legend');
    }
  }
};
</script>


